import type { AccessTokenResponse, TokenClaims, TokenData } from "@/types/auth";

import { STORAGE_KEY } from "./constants";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";
const NORMALIZED_API_BASE_URL = API_BASE_URL.replace(/\/+$/, "");
const AUTH_BASE_URL = NORMALIZED_API_BASE_URL.endsWith("/auth")
  ? NORMALIZED_API_BASE_URL
  : `${NORMALIZED_API_BASE_URL}/auth`;

/** Base64url segment → standard base64 with `=` padding for `atob`. */
function base64UrlPayloadToBase64(segment: string): string {
  let b64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  b64 += "=".repeat((4 - (b64.length % 4)) % 4);
  return b64;
}

export function decodeJwt(jwt: string): TokenClaims {
  try {
    const parts = jwt.split(".");
    if (parts.length < 2) throw new Error("missing payload segment");
    const payload = parts[1];
    const padded = base64UrlPayloadToBase64(payload);
    return JSON.parse(atob(padded)) as TokenClaims;
  } catch (e) {
    const msg = e instanceof Error ? e.message : "decode failed";
    throw new Error(`Invalid JWT: ${msg}`);
  }
}

/** Cryptographically random OAuth `state` (CSRF protection for auth code flow). */
export function generateOAuthState(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const binary = Array.from(bytes, (b) => String.fromCharCode(b)).join("");
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

export function loadTokenData(): TokenData | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY.TOKEN_DATA);
    if (!raw) return null;
    return JSON.parse(raw) as TokenData;
  } catch {
    return null;
  }
}

export function saveTokenData(data: TokenData): void {
  localStorage.setItem(STORAGE_KEY.TOKEN_DATA, JSON.stringify(data));
}

export function cleanTokenData(): void {
  localStorage.removeItem(STORAGE_KEY.TOKEN_DATA);
}

export function isTokenExpired(tokenData: TokenData): boolean {
  return tokenData.claims.exp * 1000 < Date.now();
}

/**
 * Exchanges an Okta authorization code for a Northstar JWT via the backend.
 * The backend forwards the PKCE `code_verifier` to Okta, so it must be
 * supplied here.
 */
export async function exchangeWebToken(
  code: string,
  redirectUri: string,
  codeVerifier: string,
  signal?: AbortSignal,
): Promise<AccessTokenResponse> {
  const res = await fetch(`${AUTH_BASE_URL}/oauth/access_token/v1`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      oktaCode: code,
      codeVerifier,
      redirectUri,
    }),
    signal,
  });

  if (!res.ok) {
    const body = await res.text().catch(() => "Token exchange failed");
    throw new Error(body);
  }

  return res.json() as Promise<AccessTokenResponse>;
}

// --- PKCE helpers (Web Crypto, no dependencies) ---

export async function generateCodeVerifier(): Promise<string> {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return base64UrlEncode(bytes);
}

export async function computeCodeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier),
  );
  return base64UrlEncode(new Uint8Array(digest));
}

function base64UrlEncode(bytes: Uint8Array): string {
  const binary = Array.from(bytes, (b) => String.fromCharCode(b)).join("");
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}
